/**
 * Lista de entregas em cards (≤1024px) — redesenho mobile do legado.
 *
 * Mesmo conteúdo da tabela, no card compacto de 3 linhas do ListaCompacta:
 * título = cliente, selo = status do prazo, sub = ambiente, meta = lote ·
 * marceneiro · prazo (ou chegada de MDF · dias na fila, em "Em máquinas") ·
 * recebimento T/A. O toque abre o mesmo EntregaDrawer da linha da tabela.
 * Sem animação por card: a fila passa de 400 linhas.
 */
import React from "react";
import { BalaoComentarios } from "../../components/HistoricoAmbiente";
import {
  CardItem,
  CardList,
  CardMeta,
  CardSub,
  CardTitulo,
  CardTopo,
  CardVazio,
} from "../../components/ListaCompacta/styles";
import { formatarData } from "../../utils/calculos";
import {
  parseMarceneiro,
  statusAcabamentoAmbiente,
  statusTerceirosAmbiente,
} from "../../utils/entregas";
import { EMPTY_VALUE } from "./linhasEntrega";
import { StatusBadge } from "./styles";

const TONS_DE_BARRA = new Set(["danger", "warning", "success", "info"]);

// Ano com 2 dígitos na meta ("10/11/26"): 3 datas por card com ano cheio
// empurravam a linha para duas.
export function dataCurta(valor) {
  const texto = valor ? formatarData(valor) : "";
  if (!texto || typeof texto !== "string") return EMPTY_VALUE;
  return texto.replace(/\/(\d{2})(\d{2})$/, "/$2");
}

function rotuloRecebimento(status) {
  if (!status || !status.aplicavel) return null;
  return status.completo ? "ok" : "pend.";
}

function paresDaMeta(linha, mostrarMaquinas) {
  const pares = [];
  if (linha.numeroLote && linha.numeroLote !== EMPTY_VALUE) {
    pares.push(["lote", linha.numeroLote]);
  }
  if (mostrarMaquinas) {
    pares.push(["mdf", linha.chegadaMdf ? dataCurta(linha.chegadaMdf) : EMPTY_VALUE]);
    if (linha.diasNaFilaMaquinas != null) {
      pares.push(["fila", `${Math.round(linha.diasNaFilaMaquinas)} du`]);
    }
  } else {
    const marceneiro = parseMarceneiro(linha.marceneiro);
    pares.push(["marc.", marceneiro.semMarceneiro ? "sem" : marceneiro.nome]);
    pares.push(["entrega", dataCurta(linha.dataLimite)]);
  }
  if (linha.previsao) pares.push(["", "sem lote"]);
  const terceiros = rotuloRecebimento(statusTerceirosAmbiente(linha.raw));
  if (terceiros) pares.push(["terc.", terceiros]);
  const acabamento = rotuloRecebimento(statusAcabamentoAmbiente(linha.raw));
  if (acabamento) pares.push(["acab.", acabamento]);
  return pares;
}

function aoTeclar(event, abrir) {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    abrir();
  }
}

export function CardEntrega({ linha, mostrarMaquinas = false, onAbrir }) {
  const abrir = () => onAbrir(linha);
  const barra = TONS_DE_BARRA.has(linha.statusVariant) ? linha.statusVariant : "default";
  return (
    <CardItem
      $clicavel
      $barra={barra}
      role="button"
      tabIndex={0}
      data-testid="card-entrega"
      onClick={abrir}
      onKeyDown={(event) => aoTeclar(event, abrir)}
    >
      <CardTopo>
        <CardTitulo title={linha.cliente}>{linha.cliente}</CardTitulo>
        {linha.status && (
          <span className="selo">
            <StatusBadge $tone={linha.statusVariant}>
              {linha.status}
            </StatusBadge>
          </span>
        )}
      </CardTopo>
      <CardSub title={linha.ambiente}>
            {linha.ambiente}
            <BalaoComentarios row={linha.raw} />
          </CardSub>
      <CardMeta>
        {paresDaMeta(linha, mostrarMaquinas).map(([rotulo, valor], i) => (
          <span className="par" key={`${rotulo}-${i}`}>
            {i > 0 && <span className="sep">· </span>}
            {rotulo && <span className="k">{rotulo}</span>} <b>{valor}</b>
          </span>
        ))}
      </CardMeta>
    </CardItem>
  );
}

export default function ListaCards({ linhas, mostrarMaquinas, onAbrir, vazio }) {
  if (!linhas || linhas.length === 0) {
    return (
      <CardList>
        <CardVazio>{vazio}</CardVazio>
      </CardList>
    );
  }
  return (
    <CardList data-pdf="lista-entregas">
      {linhas.map((linha) => (
        <CardEntrega
          key={linha.id}
          linha={linha}
          mostrarMaquinas={mostrarMaquinas}
          onAbrir={onAbrir}
        />
      ))}
    </CardList>
  );
}
